import type { Weight } from '../data/types';

export interface WeightAlert {
    type: 'danger' | 'warning' | 'info';
    message: string;
    details?: string;
}

/**
 * Analyzes weight history and returns alerts if there are concerning patterns
 */
export function analyzeWeightTrend(weights: Weight[]): WeightAlert | null {
    if (weights.length < 2) {
        return null; // Need at least 2 weights to analyze trend
    }

    // Sort by timestamp (most recent first)
    const sortedWeights = [...weights].sort((a, b) => b.timestamp - a.timestamp);

    const mostRecent = sortedWeights[0];
    const previous = sortedWeights[1];

    const weightChange = mostRecent.weightGrams - previous.weightGrams;
    const daysBetween = (mostRecent.timestamp - previous.timestamp) / (1000 * 60 * 60 * 24);

    // Weight loss is always concerning
    if (weightChange < 0) {
        return {
            type: 'danger',
            message: '⚠️ Weight Loss Detected',
            details: `Lost ${Math.abs(weightChange)}g since last weigh-in. Contact your foster coordinator immediately.`
        };
    }

    // No weight gain over multiple days
    if (weightChange === 0 && daysBetween >= 1) {
        return {
            type: 'warning',
            message: '⚠️ No Weight Gain',
            details: 'No weight gain detected. Monitor closely and contact your foster coordinator if this continues.'
        };
    }

    // Insufficient weight gain (should be 10-15g per day for bottle babies)
    if (daysBetween >= 1) {
        const dailyGain = weightChange / daysBetween;

        if (dailyGain < 7) {
            return {
                type: 'warning',
                message: '⚠️ Low Weight Gain',
                details: `Gaining only ${dailyGain.toFixed(1)}g per day. Healthy kittens should gain 10-15g daily. Monitor closely.`
            };
        }
    }

    // Check last 3 weights for declining trend
    if (sortedWeights.length >= 3) {
        const last3 = sortedWeights.slice(0, 3);
        const isDecreasing = last3[0].weightGrams < last3[1].weightGrams &&
            last3[1].weightGrams < last3[2].weightGrams;

        if (isDecreasing) {
            return {
                type: 'danger',
                message: '⚠️ Declining Weight Trend',
                details: 'Weight has decreased over the last 3 weigh-ins. Contact your foster coordinator immediately.'
            };
        }

        // Check for stagnant weight over 3+ weigh-ins
        const allSame = last3.every(w => Math.abs(w.weightGrams - last3[0].weightGrams) < 3);
        const timeSpan = (last3[0].timestamp - last3[2].timestamp) / (1000 * 60 * 60 * 24);

        if (allSame && timeSpan >= 2) {
            return {
                type: 'warning',
                message: '⚠️ Stagnant Weight',
                details: 'No significant weight change over multiple days. Contact your foster coordinator if this continues.'
            };
        }
    }

    return null; // No alerts
}